import { ApiFetch } from '../../../shared/utils/fetch';

// Mirrors order-service's CustomOrderStatus (PE-1892). The merchant manages the
// order through these states; ORDER_CONFIRMED and ABANDONED are terminal.
export type CustomOrderStatus =
  | 'DRAFT'
  | 'MEDIA_PROCESSING'
  | 'AWAITING_PAYMENT'
  | 'ORDER_CONFIRMED'
  | 'ABANDONED';

export interface CustomOrderAsset {
  id: string;
  objectKey: string;
  bucket: string;
  mimeType: string;
  assetType: 'image' | 'video' | 'audio' | string;
  /** original | processing | done | failed */
  transcodeState?: string | null;
  /** Short-lived signed READ url minted by the BFF (osprey-api enriches the detail). */
  readUrl?: string | null;
  readUrlExpiresAt?: string | null;
}

export interface CustomOrderStatusHistory {
  id: string;
  status: CustomOrderStatus;
  note?: string | null;
  createdAt: string;
}

/** Field definition snapshot captured at intake (labels survive later schema edits). */
export interface CustomFieldSnapshot {
  key: string;
  label?: string;
  type?: string;
  order?: number;
}

export interface CustomOrder {
  id: string;
  tenantId: string;
  solutionId: string;
  customerId?: string | null;
  customerEmail: string;
  status: CustomOrderStatus;
  amountMinor?: number | null;
  currency: string;
  bookingId?: string | null;
  invoiceId?: string | null;
  lastActivityAt: string;
  createdAt: string;
  assets?: CustomOrderAsset[];
}

export interface CustomOrderDetail extends CustomOrder {
  intake?: Record<string, unknown>;
  fieldSchemaSnapshot?: CustomFieldSnapshot[] | null;
  history?: CustomOrderStatusHistory[];
}

/** Per-asset-type upload size limits in BYTES (media-service MerchantMediaPolicy). */
export interface Guardrails {
  imageMaxBytes: number;
  videoMaxBytes: number;
  audioMaxBytes: number;
}

export interface ListCustomOrdersParams {
  status?: CustomOrderStatus;
  q?: string;
  page?: number;
  limit?: number;
}

function toQuery(params: ListCustomOrdersParams): string {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.q) sp.set('q', params.q);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Calls the osprey-api custom-orders routes (PE-1897). The admin's selected
 * merchant is sent as the `x-tenant-id` header by ApiFetch (from selectedTenantId).
 * Guardrails are backed by media-service's MerchantMediaPolicy via the BFF.
 *
 * Note: ApiFetch.put issues a PATCH on the wire, so it maps to the pricing PATCH;
 * the guardrails PUT uses ApiFetch.legacy_put to issue a real PUT.
 */
export const customOrdersApi = {
  list: (params: ListCustomOrdersParams = {}): Promise<CustomOrder[]> =>
    ApiFetch.get<CustomOrder[]>(`/custom-orders${toQuery(params)}`),

  get: (id: string): Promise<CustomOrderDetail> =>
    ApiFetch.get<CustomOrderDetail>(`/custom-orders/${id}`),

  setPricing: (id: string, amountMinor: number, currency?: string): Promise<CustomOrder> =>
    ApiFetch.put<CustomOrder>(`/custom-orders/${id}/pricing`, { amountMinor, currency }),

  markReady: (id: string): Promise<CustomOrder> =>
    ApiFetch.post<CustomOrder>(`/custom-orders/${id}/mark-ready`, {}),

  getGuardrails: (): Promise<Guardrails> =>
    ApiFetch.get<Guardrails>(`/custom-orders/guardrails`),

  setGuardrails: (body: Guardrails): Promise<Guardrails> =>
    ApiFetch.legacy_put<Guardrails>(`/custom-orders/guardrails`, body),
};
